/**
 * Sportstech Custom JavaScript
 * Centralized file for all custom theme functionality
 * 
 * Features:
 * - Product rating scroll to reviews
 * - Mega menu interactions
 * - Sticky form footer hide logic
 * - Other custom features
 */

// ============================================
// VARIANT-SYNC (ST-FIX 2026-10-02, Wrike 4577591211)
// Concept's ProductInfo swaps only its own containers (Price, Inventory, …) on a variant change.
// Every element with `data-st-variant-sync` and a stable id (e.g. the delivery line in
// snippets/shipping-info.liquid) is copied from the re-rendered section here.
// Loaded deferred after theme.js, so theme.pubsub exists; also covers sections inserted by fetch
// (quick view), where inline scripts would not run.
// ============================================
(function () {
  if (!(window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS)) return;
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, function (payload) {
    // publish() has no try/catch -> a throw here would break the other handlers. Isolate it.
    try {
      var parsedHTML = payload && payload.data && payload.data.parsedHTML;
      if (!parsedHTML) return;
      parsedHTML.querySelectorAll('[data-st-variant-sync][id]').forEach(function (src) {
        var dst = document.getElementById(src.id);
        if (dst) dst.innerHTML = src.innerHTML;
      });
    } catch (e) {
      /* silent */
    }
  });
})();

// ============================================
// DELIVERY WINDOW (ST 2026-10-13, Wrike 4578129130, round 6)
// <st-delivery-window> from snippets/st-delivery-window.liquid (in-stock items without custom.lieferdatum).
// Liquid renders the window, but pages are cached, so it is recalculated here from the current time in
// Europe/Berlin: ships today if it is a working day before data-cutoff (hour), else on the next working day,
// arrives data-min to data-max working days later (Mon-Fri, not in data-holidays). Same text format as
// snippets/st-date-range: "14.10.–15.10.2026". Runs on connect, so it also covers the variant-sync swap above
// and the re-rendered cart drawer. QA: ?st_dw_now=2026-10-13T11:30 simulates the order time.
// ============================================
(function () {
  if (!window.customElements || customElements.get('st-delivery-window')) return;
  var DAY = 864e5;

  function berlinNow() {
    var q = /[?&]st_dw_now=(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}))?/.exec(window.location.search);
    if (q) return { day: Date.UTC(+q[1], +q[2] - 1, +q[3]), hour: q[4] ? +q[4] : 0 };
    var p = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23'
    })
      .formatToParts(new Date())
      .forEach(function (x) {
        p[x.type] = x.value;
      });
    return { day: Date.UTC(+p.year, +p.month - 1, +p.day), hour: +p.hour };
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  class StDeliveryWindow extends HTMLElement {
    connectedCallback() {
      try {
        this.update();
      } catch (e) {
        /* keep the server-rendered window */
      }
    }

    update() {
      var min = parseInt(this.dataset.min, 10);
      var max = parseInt(this.dataset.max, 10);
      var cutoff = parseInt(this.dataset.cutoff, 10);
      if (!(min > 0) || !(max >= min) || isNaN(cutoff)) return;
      var holidays = (this.dataset.holidays || '').split(',');
      var now = berlinNow();
      var shipped = false;
      var n = 0;
      var from = null;
      var to = null;
      for (var i = 0; i <= 45 && !to; i++) {
        var day = new Date(now.day + i * DAY);
        var wd = day.getUTCDay();
        if (wd === 0 || wd === 6 || holidays.indexOf(day.toISOString().slice(0, 10)) !== -1) continue;
        if (i === 0 && now.hour >= cutoff) continue;
        if (!shipped) {
          shipped = true;
          continue;
        }
        n++;
        if (n === min) from = day;
        if (n === max) to = day;
      }
      if (!from || !to) return;
      var dm = function (d) {
        return pad(d.getUTCDate()) + '.' + pad(d.getUTCMonth() + 1) + '.';
      };
      var fromYear = from.getUTCFullYear();
      var toYear = to.getUTCFullYear();
      var text = dm(from) + (fromYear === toYear ? '' : fromYear) + '–' + dm(to) + toYear;
      if (this.textContent === text) return;
      // Wrike 4585234777: one-year ranges keep the year in .st-date-year (hidden on the desktop PDP), as snippets/st-date-year.
      if (fromYear !== toYear) {
        this.textContent = text;
        return;
      }
      var year = document.createElement('span');
      year.className = 'st-date-year';
      year.textContent = toYear;
      this.textContent = dm(from) + '–' + dm(to);
      this.appendChild(year);
    }
  }

  customElements.define('st-delivery-window', StDeliveryWindow);
})();

// ============================================
// KLAVIYO POP-UP vs. CART DRAWER (ST 2026-10-10, Wrike 4582076177)
// The Klaviyo newsletter pop-up (forms WAbSNf desktop / W8rkSq mobile) must never show while the cart drawer is
// open. Klaviyo only blocks cart URLs, the drawer keeps the URL. sportstech-custom.css hides it while
// #CartDrawer[open]; here it is closed via its own close button (Klaviyo then counts it as closed and waits
// 15 days, accepted by Ali): when it opens over the open drawer, and when the drawer opens over it.
// Selectors and the klaviyoForms event checked in the live DOM (2026-10-10). Embedded forms (footer) are untouched.
// ============================================
(function () {
  var POPUP_CLOSE = '[role="dialog"][aria-label="POPUP Form"] .klaviyo-close-form';

  function drawerOpen() {
    var drawer = document.getElementById('CartDrawer');
    return !!(drawer && drawer.hasAttribute('open'));
  }

  // The pop-up markup can render a moment after the "open" event -> retry briefly.
  function closePopup(tries) {
    var button = document.querySelector(POPUP_CLOSE);
    if (button) {
      button.click();
      return;
    }
    if (tries > 0) setTimeout(function () { closePopup(tries - 1); }, 100);
  }

  // On close Klaviyo hands focus back to where it was before the pop-up (outside the drawer) -> back into the drawer.
  function refocusDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawerOpen() || drawer.contains(document.activeElement)) return;
    var target = drawer.focusElement;
    if (target) target.focus();
  }

  window.addEventListener('klaviyoForms', function (event) {
    if (!event.detail || !drawerOpen()) return;
    if (event.detail.type === 'open') closePopup(20);
    if (event.detail.type === 'close') setTimeout(refocusDrawer, 50);
  });

  document.addEventListener('drawer:afterShow', function (event) {
    if (event.target && event.target.id === 'CartDrawer') closePopup(0);
  });
})();

// ============================================
// KLAVIYO POP-UP vs. ALHENA CHAT (ST 2026-10-25, Wrike 4591209264)
// No Klaviyo pop-up while the Alhena chat is open. html.st-alhena-chat-open follows the SDK events
// (opened/restored = open, closed/minimized = closed); sportstech-custom.css hides the pop-up and teaser while it is set.
// Hidden, never closed: Klaviyo counts no dismissal and shows the pop-up once the chat is closed.
// The SDK can load late (consent) and React re-patches gleenWidget -> register with polling and re-check, as
// st-tracking-events does. Callbacks return false (true would suppress Alhena's default handling, see st-open-chat.js).
// ============================================
(function () {
  var OPEN_CLASS = 'st-alhena-chat-open';
  var POPUP = '[role="dialog"][aria-label="POPUP Form"]';

  function setOpen(open) {
    document.documentElement.classList.toggle(OPEN_CLASS, open);
    if (open) refocusChat();
  }

  // Klaviyo moves focus into its pop-up when it opens; while it is hidden, focus belongs in the chat.
  function refocusChat() {
    var active = document.activeElement;
    if (!active || !active.closest || !active.closest(POPUP)) return;
    var input = document.querySelector('#helix-chat-container textarea');
    if (input) input.focus();
    else active.blur();
  }

  // Chat window = second child of the container; it has no size while closed (checked in the live DOM 2026-10-25).
  function chatWindowOpen() {
    var container = document.getElementById('helix-chat-container');
    var chatWindow = container && container.children[1];
    if (!chatWindow) return false;
    var rect = chatWindow.getBoundingClientRect();
    return window.getComputedStyle(chatWindow).visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  // Once an SDK event arrived it is the truth; the DOM check only covers the time before.
  var sawEvent = false;

  // Events can arrive out of order (e.g. "opened" after a quick close while the chat still loads). After each event
  // the chat window is checked again; a mismatch has to hold for three checks before the DOM wins.
  var verifyTimer = null;
  function verify(tries) {
    clearTimeout(verifyTimer);
    verifyTimer = setTimeout(function () {
      var open = chatWindowOpen();
      if (open === document.documentElement.classList.contains(OPEN_CLASS)) return;
      if (tries > 0) verify(tries - 1);
      else setOpen(open);
    }, 1500);
  }

  function onOpen() {
    sawEvent = true;
    setOpen(true);
    verify(2);
    return false;
  }

  function onClose() {
    sawEvent = true;
    setOpen(false);
    verify(2);
    return false;
  }

  function syncFromDom() {
    if (!sawEvent && chatWindowOpen()) setOpen(true);
  }

  var callbacks = {
    'widget:opened': onOpen,
    'widget:restored': onOpen,
    'widget:closed': onClose,
    'widget:minimized': onClose
  };

  function register() {
    var w = window.gleenWidget;
    if (!w || typeof w.on !== 'function' || !w.eventCallbacks) return false;
    Object.keys(callbacks).forEach(function (name) {
      try {
        var list = w.eventCallbacks[name];
        if (list && list.indexOf(callbacks[name]) === -1) w.on(name, callbacks[name]);
      } catch (err) {}
    });
    return true;
  }

  // The pop-up takes focus at a varying moment after its "open" event (desktop later than mobile) -> catch the focus
  // itself. Capped, so a focus trap that pulls focus back cannot start a loop.
  var refocusCount = 0;
  document.addEventListener('focusin', function (event) {
    if (!document.documentElement.classList.contains(OPEN_CLASS)) return;
    if (!event.target || !event.target.closest || !event.target.closest(POPUP)) return;
    if (++refocusCount > 10) return;
    setTimeout(function () { refocusCount = Math.max(0, refocusCount - 1); }, 2000);
    refocusChat();
  }, true);

  var started = Date.now();
  (function attempt() {
    if (register()) {
      // The chat can be restored open from the previous page before our callbacks exist
      syncFromDom();
      [1000, 3000, 8000, 15000].forEach(function (ms) {
        setTimeout(function () {
          register();
          syncFromDom();
        }, ms);
      });
      return;
    }
    var elapsed = Date.now() - started;
    if (elapsed >= 120000) return;
    setTimeout(attempt, elapsed < 10000 ? 250 : 2000);
  })();
})();

document.addEventListener('DOMContentLoaded', function() {
  // ============================================
  // 1. PRODUCT RATING - SCROLL TO REVIEWS
  // ============================================
  const ratingBtn = document.querySelector('[data-scroll-to-reviews]');
  if (ratingBtn) {
    ratingBtn.addEventListener('click', function(e) {
      e.preventDefault();
      const reviewsSection = document.querySelector('.section-reviews-io-custom');
      if (reviewsSection) {
        // Calculate offset for fixed header and sticky elements
        const fixedHeader = document.querySelector('header, .site-header, [role="banner"]');
        const scrollOffset = fixedHeader ? fixedHeader.offsetHeight + 30 : 100; // 30px extra padding
        
        const elementPosition = reviewsSection.getBoundingClientRect().top + window.scrollY;
        const targetPosition = elementPosition - scrollOffset;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  }

  // ============================================
  // 2. STICKY FORM FOOTER HIDE - Mobile Only
  
  (function () {
    'use strict';

    var body = document.body;
    var mobileOnly = window.matchMedia ? window.matchMedia('(max-width: 1279px)') : null;

    // Early return if no mobile query support or on desktop
    if (!mobileOnly || mobileOnly.matches === false) return;

    var productFormWrapper = document.querySelector('.product-form-wrapper');
    var footerGroup = document.querySelector('.footer-group');

    if (!productFormWrapper && !footerGroup) return;

    // Use Intersection Observer if available (modern browsers)
    if ('IntersectionObserver' in window) {
      var updateStickyFormVisibility = function () {
        var productFormInViewport = false;
        var footerInViewport = false;

        if (productFormWrapper) {
          var productFormRect = productFormWrapper.getBoundingClientRect();
          productFormInViewport = productFormRect.top < window.innerHeight && productFormRect.bottom >= 0;
        }

        if (footerGroup) {
          var footerRect = footerGroup.getBoundingClientRect();
          footerInViewport = footerRect.top < window.innerHeight && footerRect.bottom >= 0;
        }

        // Hide sticky form if EITHER element is in viewport
        if (productFormInViewport || footerInViewport) {
          body.classList.add('st-sticky-form-hidden');
        } else {
          body.classList.remove('st-sticky-form-hidden');
        }
      };

      var observer = new IntersectionObserver(updateStickyFormVisibility, { threshold: 0 });

      if (productFormWrapper) observer.observe(productFormWrapper);
      if (footerGroup) observer.observe(footerGroup);
    } else {
      // Fallback for older browsers: check on scroll
      function checkElementsVisibility() {
        var productFormInViewport = false;
        var footerInViewport = false;

        if (productFormWrapper) {
          var productFormRect = productFormWrapper.getBoundingClientRect();
          productFormInViewport = productFormRect.top < window.innerHeight && productFormRect.bottom >= 0;
        }

        if (footerGroup) {
          var footerRect = footerGroup.getBoundingClientRect();
          footerInViewport = footerRect.top < window.innerHeight && footerRect.bottom >= 0;
        }

        // Hide sticky form if EITHER element is in viewport
        if (productFormInViewport || footerInViewport) {
          body.classList.add('st-sticky-form-hidden');
        } else {
          body.classList.remove('st-sticky-form-hidden');
        }
      }

      window.addEventListener('scroll', checkElementsVisibility, { passive: true });
      window.addEventListener('resize', checkElementsVisibility, { passive: true });

      // Initial check
      checkElementsVisibility();
    }
  })();

  // ============================================
  // 3. ALHENA CHAT WIDGET VISIBILITY CONTROLLER
  // ============================================
  (function initAlhenaVisibility() {
    var productInfo = document.querySelector('product-info, .product__info');
    var productGallery = document.querySelector('.product__gallery-container');
    var isPdp = !!(productInfo || productGallery);

    var cartDrawer = document.querySelector('cart-drawer');
    // Non-PDP pages can mark areas the bubble must not cover (Top-Deals page: hero countdown and product grid,
    // Wrike 4578334610): hidden while a zone is in view, mobile and desktop.
    var hideZones = Array.prototype.slice.call(document.querySelectorAll('[data-st-chat-hide-zone]'));
    var hasZones = hideZones.length > 0;

    // Phones in landscape (and other short viewports): the expanded nudge covers header and sticky ATC
    var shortMq = window.matchMedia ? window.matchMedia('(max-height: 500px)') : null;
    var isCartPage = document.body.classList.contains('template-cart');
    var isProductTemplate = document.body.classList.contains('template-product');
    var isChatHidden = null;

    // Launcher from first paint on every page (also PDPs, Wrike 4595618845, replaces the PDP scroll gate of D-49).
    // The theme owns the nudge: not before 30 s on the page, once per browser session, and on product pages only
    // while it would not cover price or add-to-cart. Alhena's own timer nudge (dashboard) is folded back otherwise.
    var NUDGE_DELAY = 30000;
    var NUDGE_SESSION_KEY = 'st-alhena-nudge-shown';
    // Expanded bubble above the launcher, measured on live 2026-10-30: 235x109 (mobile), 235x174 (desktop)
    var NUDGE_W = 240;
    var NUDGE_H = 180;
    var nudgeDue = false;
    var nudgeOwn = false; // the theme opened the nudge on this page view
    var nudgeFolded = false; // the theme folded its own nudge and reopens it when the way is clear
    // CSS gate (sportstech-custom.css): the nudge bubble stays invisible until this class is set, so neither Alhena's
    // own timer nor a late fold can show it before the 30 s (QA round 1: a nudge appeared after 19-25 s on the PDP).
    var NUDGE_ON_CLASS = 'st-alhena-nudge-on';
    var launcherLogged = false;
    var heldBackLogged = false;
    // Timings for QA in the browser console, seconds since navigation start
    function logChat(msg) {
      var t = window.performance && performance.now ? (performance.now() / 1000).toFixed(1) : '?';
      if (window.console && console.info) console.info('[st-alhena] ' + msg + ' at ' + t + ' s');
    }
    // Queried on every check: the theme re-renders the buy buttons after load (variant / stock updates)
    function getBuyboxTargets() {
      if (!isProductTemplate || !productInfo) return [];
      return Array.prototype.slice.call(productInfo.querySelectorAll('.price, .product-form__buttons'));
    }

    function nudgeShownInSession() {
      try {
        return window.sessionStorage.getItem(NUDGE_SESSION_KEY) === '1';
      } catch (e) {
        return false;
      }
    }

    function markNudgeShown() {
      try {
        window.sessionStorage.setItem(NUDGE_SESSION_KEY, '1');
      } catch (e) {}
    }

    // The first child is the launcher bar; it only carries text while the nudge bubble is expanded
    function getLauncherBar() {
      var helixChat = document.getElementById('helix-chat-container');
      return helixChat && helixChat.firstElementChild;
    }

    // textContent, not innerText: the CSS gate and the newsletter pop-up rule hide the bubble with visibility
    function isNudgeOpen() {
      var bar = getLauncherBar();
      return !!(bar && bar.textContent.trim());
    }

    // Klaviyo newsletter pop-up: the nudge does not open while it is shown (an open nudge is hidden under it by
    // sportstech-custom.css and comes back when the pop-up closes)
    function isNewsletterOpen() {
      var popup = document.querySelector("[role='dialog'][aria-label='POPUP Form']");
      return !!(popup && popup.getBoundingClientRect().height > 0);
    }

    // Would the expanded bubble cover price or add-to-cart in the buybox?
    function nudgeCoversBuybox() {
      var targets = getBuyboxTargets();
      if (!targets.length) return false;
      var bar = getLauncherBar();
      if (!bar) return false;
      var zone;
      if (isNudgeOpen()) {
        zone = bar.getBoundingClientRect();
      } else {
        var button = bar.lastElementChild || bar;
        var b = button.getBoundingClientRect();
        if (!b.width) return false;
        zone = { left: b.right - NUDGE_W, right: b.right, top: b.bottom - NUDGE_H, bottom: b.bottom };
      }
      return targets.some(function (el) {
        var r = el.getBoundingClientRect();
        return r.width > 0 && r.left < zone.right && r.right > zone.left && r.top < zone.bottom && r.bottom > zone.top;
      });
    }

    // The launcher itself crosses price / add-to-cart for a short scroll range on PDPs: fade it out meanwhile
    var isDodging = false;
    function updateLauncherDodge() {
      var dodge = false;
      var bar = getLauncherBar();
      var button = bar && bar.lastElementChild;
      var targets = getBuyboxTargets();
      if (button && targets.length && !document.documentElement.classList.contains('st-alhena-chat-open')) {
        var b = button.getBoundingClientRect();
        dodge = b.width > 0 && targets.some(function (el) {
          var r = el.getBoundingClientRect();
          return r.width > 0 && r.left < b.right && r.right > b.left && r.top < b.bottom && r.bottom > b.top;
        });
      }
      if (dodge === isDodging) return;
      isDodging = dodge;
      document.body.classList.toggle('st-chat-dodge', dodge);
    }

    // Launcher only, no expanded nudge bubble: short viewports (phones in landscape), buybox under the bubble
    function shouldSuppressNudge() {
      return !!(shortMq && shortMq.matches) || nudgeCoversBuybox();
    }

    function hideNudge() {
      var w = window.gleenWidget;
      if (w && typeof w.hideNudge === 'function') w.hideNudge();
    }

    // Opens the nudge once the 30 s are up, or reopens the theme's own nudge after it was folded
    function updateNudge() {
      var w = window.gleenWidget;
      if (!w || typeof w.showNudge !== 'function' || isChatHidden !== false) return;
      if (nudgeOwn) {
        if (isNudgeOpen()) {
          if (shouldSuppressNudge()) {
            nudgeFolded = true;
            hideNudge();
          }
        } else if (nudgeFolded && !shouldSuppressNudge() && !isNewsletterOpen()) {
          nudgeFolded = false;
          w.showNudge();
          logChat('nudge reopened');
        }
        return;
      }
      if (!nudgeDue || nudgeShownInSession() || shouldSuppressNudge() || isNewsletterOpen()) return;
      nudgeOwn = true;
      document.documentElement.classList.add(NUDGE_ON_CLASS);
      w.showNudge();
      logChat('nudge shown (theme, 30 s rule)');
    }

    // Alhena renders its container and shows the timed nudge after our first
    // applyVisibility() call, so re-apply the current state on every widget change.
    function enforceChatState() {
      var helixChat = document.getElementById('helix-chat-container');
      if (!helixChat || isChatHidden === null) return;

      if (isChatHidden) {
        if (helixChat.style.display !== 'none') helixChat.style.display = 'none';
        return;
      }

      if (!launcherLogged && getLauncherBar() && getLauncherBar().lastElementChild) {
        launcherLogged = true;
        logChat('launcher shown');
      }

      if (!isNudgeOpen()) return;
      if (!nudgeOwn) {
        // Alhena's own timer (15 s in the dashboard) or a nudge already shown in this session
        if (!heldBackLogged) {
          heldBackLogged = true;
          logChat('Alhena nudge held back (' + (nudgeShownInSession() ? 'already shown in this session' : 'before 30 s') + ')');
        }
        hideNudge();
        return;
      }
      markNudgeShown();
      if (shouldSuppressNudge()) {
        nudgeFolded = true;
        hideNudge();
      }
    }

    // Short viewports: lift the launcher just above the sticky ATC card while it is shown.
    // Card heights differ per template (77-90px), so measure instead of a fixed offset.
    var stickyCard = document.querySelector('.product-sticky-form__card');
    var chatLift = 0;
    function updateChatLift() {
      var lift = 0;
      if (stickyCard && shortMq && shortMq.matches) {
        var cardStyle = window.getComputedStyle(stickyCard);
        var cardRect = stickyCard.getBoundingClientRect();
        if (cardStyle.visibility !== 'hidden' && parseFloat(cardStyle.opacity) > 0 && cardRect.height > 0) {
          lift = Math.round(window.innerHeight - cardRect.top + 8);
        }
      }
      if (lift === chatLift) return;
      chatLift = lift;
      if (lift > 0) {
        document.body.style.setProperty('--st-chat-lift', lift + 'px');
        document.body.classList.add('st-chat-lift');
      } else {
        document.body.classList.remove('st-chat-lift');
      }
    }
    if (stickyCard) {
      // The card fades in/out after the theme's own observer fires, i.e. after our scroll handler
      stickyCard.addEventListener('transitionend', updateChatLift);
    }

    var chatObserver = null;
    function observeChat() {
      var helixChat = document.getElementById('helix-chat-container');
      if (!helixChat || chatObserver || !window.MutationObserver) return !!helixChat;
      chatObserver = new MutationObserver(enforceChatState);
      chatObserver.observe(helixChat, { childList: true, subtree: true, characterData: true });
      enforceChatState();
      return true;
    }

    // The container is a direct child of <body> and can arrive late (consent retries)
    if (!observeChat() && window.MutationObserver) {
      var bodyObserver = new MutationObserver(function () {
        if (observeChat()) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.body, { childList: true });
    }

    function isCartDrawerOpen() {
      if (!cartDrawer) {
        cartDrawer = document.querySelector('cart-drawer');
      }
      return !!(
        cartDrawer &&
        (cartDrawer.hasAttribute('open') || cartDrawer.hasAttribute('active'))
      );
    }

    // ST 2026-10-31 (Wrike 4595804059): the bubble hides behind every overlay, not only the cart drawer.
    // Generic, so new sheets need no new ticket: every theme drawer/modal (drawer-element, modal-element, x-modal,
    // cart/menu/search drawer, PDP sheets, facet drawer, financing ...) extends Concept's ModalElement, plus native
    // <dialog open>, foreign [role=dialog][aria-modal=true][open], the Concept scroll lock (body.has-modal-open),
    // the collection compare drawer (body.st-compare-open) and the PhotoSwipe gallery lightbox (.pswp).
    // Not the Klaviyo pop-up: sportstech-custom.css lifts the launcher above it on purpose (Wrike 4591209264).
    function isOverlayOpen() {
      var body = document.body;
      if (body.classList.contains('has-modal-open') || body.classList.contains('st-compare-open')) return true;
      if (document.querySelector('.pswp')) return true;
      var ModalBase = window.customElements && window.customElements.get('modal-element');
      var openNodes = document.querySelectorAll('[open]');
      for (var i = 0; i < openNodes.length; i++) {
        var el = openNodes[i];
        if (el.closest('#helix-chat-container, [aria-label="POPUP Form"]')) continue;
        if (ModalBase && el instanceof ModalBase) return true;
        if (el.tagName === 'DIALOG') return true;
        if (el.getAttribute('role') === 'dialog' && el.getAttribute('aria-modal') === 'true') return true;
      }
      return false;
    }

    // A chat opened while an overlay is open (e.g. the "#open-chat" pill in the mobile menu) wins over that overlay;
    // a chat that was already open hides behind a newly opened overlay. Reset once the overlays are closed.
    var CHAT_OPEN_CLASS = 'st-alhena-chat-open';
    var chatWinsOverlay = false;
    var chatWasOpen = document.documentElement.classList.contains(CHAT_OPEN_CLASS);
    function syncChatOverride(overlayOpen) {
      var chatOpen = document.documentElement.classList.contains(CHAT_OPEN_CLASS);
      if (!chatOpen || !overlayOpen) chatWinsOverlay = false;
      else if (!chatWasOpen) chatWinsOverlay = true;
      chatWasOpen = chatOpen;
    }

    function applyVisibility(shouldHide) {
      // Prevent redundant DOM / SDK updates to eliminate scroll jitter
      if (shouldHide === isChatHidden) return;
      isChatHidden = shouldHide;

      var helixChat = document.getElementById('helix-chat-container');
      var w = window.gleenWidget;

      if (shouldHide) {
        // The theme's own nudge comes back with the launcher
        if (nudgeOwn && isNudgeOpen()) nudgeFolded = true;
        // Hide Alhena chat box & launcher/nudge
        if (helixChat) {
          helixChat.style.display = 'none';
        }
        if (w) {
          if (typeof w.hideLauncher === 'function') {
            w.hideLauncher();
          }
          if (typeof w.hideNudge === 'function') {
            w.hideNudge();
          }
        }
      } else {
        // Show/enable Alhena chat box
        if (helixChat) {
          helixChat.style.display = '';
        }
        // Launcher only: the nudge follows its own rules (updateNudge)
        if (w && typeof w.showLauncher === 'function') {
          w.showLauncher();
        }
      }
      enforceChatState();
    }

    function evaluateVisibility() {
      updateChatLift();

      // 1. Hide on BOTH desktop and mobile on the cart page and when Cart Drawer is opened
      if (isCartPage || isCartDrawerOpen()) {
        applyVisibility(true);
        return;
      }

      // 1b. Any other overlay (drawer, sheet, modal, dialog, lightbox), unless the chat was opened on top of it
      var overlayOpen = isOverlayOpen();
      syncChatOverride(overlayOpen);
      if (overlayOpen && !chatWinsOverlay) {
        applyVisibility(true);
        return;
      }

      // 2. Pages with hide zones (Top-Deals page): hidden while a zone is in view.
      if (hasZones && !isPdp) {
        var zoneVh = window.innerHeight;
        applyVisibility(hideZones.some(function (zone) {
          var r = zone.getBoundingClientRect();
          return r.top < zoneVh && r.bottom >= 100;
        }));
      } else {
        // 3. All other pages, PDPs included, mobile and desktop: always show the launcher.
        applyVisibility(false);
      }
      updateLauncherDodge();
      updateNudge();
    }

    // Observe Cart Drawer mutations (instant open/close detection on desktop & mobile)
    if (cartDrawer && window.MutationObserver) {
      var drawerObserver = new MutationObserver(function () {
        evaluateVisibility();
      });
      drawerObserver.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['open', 'active']
      });
    }

    // Listen to theme drawer and cart events
    document.addEventListener('drawer:afterShow', evaluateVisibility);
    document.addEventListener('drawer:afterHide', evaluateVisibility);
    document.addEventListener('cart:refresh', evaluateVisibility);
    document.addEventListener('ajaxProduct:added', evaluateVisibility);
    document.addEventListener('modal:afterShow', evaluateVisibility);
    document.addEventListener('modal:afterHide', evaluateVisibility);

    // Overlays open/close: [open] anywhere, body classes (scroll lock, compare), direct body children (drawers are
    // appended to <body>, PhotoSwipe adds/removes .pswp), html class (chat open). Batched to one check per frame.
    if (window.MutationObserver) {
      var overlayFrame = 0;
      var onOverlayMutation = function () {
        if (overlayFrame) return;
        overlayFrame = window.requestAnimationFrame(function () {
          overlayFrame = 0;
          evaluateVisibility();
        });
      };
      // One observer per registration: observe() on the same node replaces the earlier options
      new MutationObserver(onOverlayMutation).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
      new MutationObserver(onOverlayMutation).observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      new MutationObserver(onOverlayMutation).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }

    // Pages with hide zones: observe the zones. PDPs: scroll moves the buybox under the nudge and the sticky ATC card.
    if (isPdp || hasZones) {
      if (hasZones && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function () {
          evaluateVisibility();
        }, {
          threshold: [0, 0.1, 0.2]
        });

        hideZones.forEach(function (zone) { observer.observe(zone); });
      }

      window.addEventListener('scroll', evaluateVisibility, { passive: true });
      window.addEventListener('resize', evaluateVisibility, { passive: true });
    }

    // The launcher drops to the page bottom when body.st-sticky-form-hidden flips (set by an IntersectionObserver
    // after the scroll event), which can put it on the add-to-cart button: check again on body class changes.
    if (isProductTemplate && window.MutationObserver) {
      new MutationObserver(function () {
        updateLauncherDodge();
        updateNudge();
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    function onMqChange() {
      isChatHidden = null; // force re-sync on orientation change
      evaluateVisibility();
    }
    if (shortMq) {
      if (shortMq.addEventListener) {
        shortMq.addEventListener('change', onMqChange);
      } else if (shortMq.addListener) {
        shortMq.addListener(onMqChange);
      }
    }

    // Initial check
    evaluateVisibility();

    // 30 s on this page (counted from navigation start), then the nudge may open
    // Afterwards check every second until it opened: the pop-up, the buybox or a late widget can delay it
    setTimeout(function () {
      nudgeDue = true;
      evaluateVisibility();
      var nudgeTimer = setInterval(function () {
        if (nudgeOwn || nudgeShownInSession()) {
          clearInterval(nudgeTimer);
          return;
        }
        updateNudge();
      }, 1000);
    }, Math.max(0, NUDGE_DELAY - (window.performance && performance.now ? performance.now() : 0)));

    // Hook into Alhena widget when it loads
    function onWidgetReady() {
      isChatHidden = null;
      evaluateVisibility();

      if (typeof window.gleenWidget.on === 'function') {
        try {
          window.gleenWidget.on('widget:opened', function () {
            var helixChat = document.getElementById('helix-chat-container');
            if (helixChat) helixChat.style.display = '';
          });
        } catch (e) {}
      }
    }

    if (window.gleenWidget) {
      onWidgetReady();
    } else {
      var tries = 0;
      // Up to 60 s: on slow connections the widget can arrive well after the first 10 s
      var timer = setInterval(function () {
        if (window.gleenWidget) {
          onWidgetReady();
          clearInterval(timer);
        } else if (++tries > 240) {
          clearInterval(timer);
        }
      }, 250);
    }
  })();
});
